import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaTimes,
  FaPeopleCarry,
  FaAngleRight,
  FaArrowLeft,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaRedo,
  FaTools,
  FaWarehouse,
  FaPowerOff,
} from "react-icons/fa";
import { TbTruckDelivery } from "react-icons/tb";
import { RiScales2Fill } from "react-icons/ri";
import { useFirmData } from "../../Hooks/useFirmData";
import { useFetchPlayMakerQuery } from "../../store";
import usePermission from "../../Hooks/usePermission";
import { useFetchDealerPlayMakerQuery } from "../../store/dealerRoles/devicesApi";
import useAuth from "../../Hooks/useAuth";
import { Checkbox, FormControlLabel } from "@mui/material";
import {
  useUploadManualQRMutation,
  useUploadManualQuotaMutation,
} from "../../store/storageRoles/devicesSharedApi";

// Alt bileşenler
import SearchableList from "./OperationModal/SearchableList";
import DeviceItem from "./OperationModal/DeviceItem";
import FirmItem from "./OperationModal/FirmItem";
import PlaymakerItem from "./OperationModal/PlaymakerItem";
import ServiceReasonItem from "./OperationModal/ServiceReasonItem";
import useDrawerTransition from "../../Hooks/useDrawerTransition";
import {
  DEVICE_ACTIVITY_FIELD_LABEL,
  deviceActivityStateLabel,
  deviceActivityToggleLabel,
} from "../../utils/deviceActivityLabels";

// Servis nedenlerini kategorize edelim
const MACHINE_SERVICE_REASONS = {
  // Tüm makinelerde ortak olan arızalar
  COMMON: [
    "Yazılım Güncellemesi",
    "Bakım ve Onarım",
    "Ana Kart Arızası",
    "Ekran Arızası",
    "Kartlar Arası Haberleşme Arızası",
    "Şebeke Pompası Arızası",
    "NTC(Sıcaklık Sensörü) Arızası",
  ],

  // Çay, Filtre Kahve, Çayset ve Çayset-Fincanda makinelerinde olan arızalar
  TEA_AND_COFFEE: [
    "Röle Kartı Arızası",
    "Isıtıcı(Rezistans) Arızası",
    "Şamandıra Arızası",
    "Demleme Pompası Arızası",
    "Dem Valfi Arızası",
    "Su Valfi Arızası",
    "Loadcell Arızası",
    "Damacana Pompası Arızası",
  ],

  // Fincanda ve Çayset-Fincanda makinelerinde olan arızalar
  CUP_MACHINE: [
    "Askı Kartı Arızası",
    "Su Deposu Sensörü Arızası",
    "Su Deposu Pompası Arızası",
    "Değirmen Sensörü Arızası",
    "Değirmen Motoru Arızası",
    "Değirmen Kartı Arızası",
    "Askı Sensörü(CNY) Arızası",
    "Servis Motoru Arızası",
    "Fincan Sensörü(CNY) Arızası",
    "Isıtıcı(Fişek Rezistans) Arızası",
    "Karıştırıcı Motoru Arızası",
    "Fan Motoru Arızası",
    "Fincan Su Var/Yok Sensörü Arızası",
    "Fincan Su Pompası Arızası",
  ],
};

function DeviceOperationPanel({
  open,
  title,
  devices,
  onClose,
  onConfirm,
  operation,
  deviceOperationIsLoading,
  deviceOperationIsSuccess,
  deviceOperationIsError,
  deviceOperationErrorMessage,
  deviceOperationSuccessMessage,
  selectedOperationDevice,
  setSelectedOperationDevice,
  ...props
}) {
  // Giriş/çıkış kayması — panel kapalı pozisyonda mount olur, iki frame sonra
  // açılır; kapanışta overlay de birlikte söner.
  const { mounted, entered } = useDrawerTransition(open);
  const { auth } = useAuth();
  const token = auth.accessToken;

  // Firma verileri
  const { firms } = useFirmData(token);

  // Plasiyer verileri — bayi kullanıcıları storage endpoint'ine 403 aldığından
  // rol bazlı hook seçilir (bayi kendi plasiyerlerini görür).
  const isDealer = auth?.role === "dealer";
  // Uçlar izin de ister (merkez users.read, bayi devices.moveToPlaymaker) — #414
  const { hasPermission } = usePermission();
  const storagePlaymakersResult = useFetchPlayMakerQuery(token, {
    skip: isDealer || !hasPermission("users.read"),
  });
  const dealerPlaymakersResult = useFetchDealerPlayMakerQuery(token, {
    skip: !isDealer || !hasPermission("devices.moveToPlaymaker"),
  });
  const ResultPlaymakers = isDealer ? dealerPlaymakersResult : storagePlaymakersResult;
  const Playmakers = ResultPlaymakers.data?.data?.playmakers || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [firmSearchTerm, setFirmSearchTerm] = useState("");
  const [playmakerSearchTerm, setPlaymakerSearchTerm] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [selectedPlaymaker, setSelectedPlaymaker] = useState(null);
  const [description, setDescription] = useState("");
  const [connectDuringOperationChoice, setConnectDuringOperation] = useState(true);
  // Bayide makine bağlantısı her zaman zorunlu — tercih sorulmaz (kullanıcı
  // kararı 2026-10-09, #290). Depo panelinde çevrimdışı akış seçilebilir.
  const connectDuringOperation = isDealer || connectDuringOperationChoice;
  const [isActiveOnTransfer, setIsActiveOnTransfer] = useState(false);

  // Cihaz bayinin eline geçiyorsa (bayi panelinde servis/plasiyer/depo, depo
  // panelinde hedef firma bayi ise) her zaman KAPALI alınır — aktiflik
  // tercihi sorulmaz, sunucu da bunu zorlar (kullanıcı kararı 2026-08-17).
  const transferGoesToDealer =
    (isDealer && ["Servis", "Plasiyer", "Depo"].includes(operation)) ||
    (operation === "Firma" && selectedFirm?.firmType === "dealer");
  const effectiveIsActiveOnTransfer = transferGoesToDealer
    ? false
    : isActiveOnTransfer;

  // İşlem adımları
  const [currentStep, setCurrentStep] = useState(1); // 1: Seçim, 2: Onay, 3: Sonuç
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null); // "success" veya "error"
  const [errorMessage, setErrorMessage] = useState("");

  // Servis nedenleri için state'leri güncelleyelim
  const [serviceReasonSearchTerm, setServiceReasonSearchTerm] = useState("");
  const [serviceReasons, setServiceReasons] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState([]);

  // Manuel Kota için state'ler
  const [quota, setQuota] = useState(0);
  const [code, setCode] = useState("");
  const [deviceInfo, setDeviceInfo] = useState();
  const [productName, setProductName] = useState("");
  const [productQuota, setProductQuota] = useState("");
  const [productCounter, setProductCounter] = useState("");

  // Manuel Kota için API mutations
  const [uploadManualQR, resultUploadManualQR] = useUploadManualQRMutation();
  const [uploadManualQuota, resultUploadManualQuota] =
    useUploadManualQuotaMutation();

  // Manuel Kota için useEffect'ler
  useEffect(() => {
    if (resultUploadManualQR.data?.data) {
      setDeviceInfo(resultUploadManualQR.data.data);
    }
  }, [resultUploadManualQR]);

  useEffect(() => {
    if (resultUploadManualQuota.data?.data) {
      setDeviceInfo(resultUploadManualQuota.data.data);
    }
  }, [resultUploadManualQuota]);

  // Panel açıkken body'nin scroll'unu kilitle
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  // Form yeniden açıldığında değerleri sıfırla
  useEffect(() => {
    if (open) {
      if (selectedOperationDevice) {
        setSelectedDevice(selectedOperationDevice);
      } else {
        setSelectedDevice(null);
      }
      setSelectedFirm(null);
      setSelectedPlaymaker(null);
      setDescription("");
      setConnectDuringOperation(true);
      setIsActiveOnTransfer(false);
      setSearchTerm("");
      setFirmSearchTerm("");
      setPlaymakerSearchTerm("");
      setCurrentStep(1);
      setIsProcessing(false);
      setProcessResult(null);
      setErrorMessage("");
      setServiceReasonSearchTerm("");
      setSelectedReasons([]);
      setQuota(0);
      setCode("");
      setDeviceInfo(null);
      setProductName("");
      setProductQuota("");
      setProductCounter("");
    }
  }, [open]);

  // İşlem adımları için useEffect güncellemesi
  useEffect(() => {
    // İşlem durumları componentleri arasında güncelleme
    if (deviceOperationIsLoading) {
      setIsProcessing(true);
      // Süreç başladığında sonuç adımına geçiş
      if (currentStep < 3) {
        setCurrentStep(3);
      }
    } else {
      setIsProcessing(false);

      // İşlem başarılı veya başarısız olunca sonuç göster
      if (deviceOperationIsSuccess) {
        setProcessResult("success");
      } else if (deviceOperationIsError) {
        setProcessResult("error");
        setErrorMessage(
          deviceOperationErrorMessage || "İşlem sırasında bir hata oluştu"
        );
      }
    }
  }, [
    deviceOperationIsLoading,
    deviceOperationIsSuccess,
    deviceOperationIsError,
    deviceOperationErrorMessage,
    currentStep,
  ]);

  // Cihaz seçildiğinde servis nedenlerini güncelle
  useEffect(() => {
    if (selectedDevice && operation === "Servis") {
      let reasons = [...MACHINE_SERVICE_REASONS.COMMON]; // Ortak arızaları ekle

      // Makine tipine göre ek arızaları ekle
      const deviceType =
        selectedDevice.deviceInfo?.deviceName || selectedDevice.deviceName;

      if (deviceType) {
        if (
          ["Çay", "Filtre Kahve", "Çayset", "Çayset-Fincanda"].includes(
            deviceType
          )
        ) {
          reasons = [...reasons, ...MACHINE_SERVICE_REASONS.TEA_AND_COFFEE];
        }

        if (["Fincanda", "Çayset-Fincanda"].includes(deviceType)) {
          reasons = [...reasons, ...MACHINE_SERVICE_REASONS.CUP_MACHINE];
        }
      }

      // Nedenleri alfabetik sırala
      reasons.sort();

      // State'i güncelle
      setServiceReasons(reasons);
      // Seçili nedenleri sıfırla
      setSelectedReasons([]);
    }
  }, [selectedDevice, operation]);

  const filteredFirms =
    firms?.filter((firm) => {
      if (!firm) return false;
      const firmName = firm.firmName || "";
      const bayserNo = firm.bayserNo || "";
      const searchLower = firmSearchTerm.toLowerCase();

      return (
        firmName.toLowerCase().includes(searchLower) ||
        bayserNo.toLowerCase().includes(searchLower)
      );
    }) || [];

  const filteredPlaymakers =
    Playmakers?.filter((playmaker) => {
      if (!playmaker) return false;
      const fullName = `${playmaker.name} ${playmaker.lastName}`.toLowerCase();
      return fullName.includes(playmakerSearchTerm.toLowerCase());
    }) || [];

  // Servis nedenleri için filtreleme
  const filteredServiceReasons = serviceReasons.filter((reason) =>
    reason.toLowerCase().includes(serviceReasonSearchTerm.toLowerCase())
  );

  // Servis nedeni seçme/kaldırma işlevi
  const toggleServiceReason = (reason) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter((item) => item !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  // Bir sonraki adıma geç
  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  // Bir önceki adıma dön
  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Seçilen nedenleri description ile birleştiren fonksiyon
  const getCombinedDescription = () => {
    if (selectedReasons.length === 0) return `\nAçıklama:${description}`;

    // Servis nedenlerini virgülle birleştir
    const reasonsText = selectedReasons.join(", ") + ".";
    // İlk harfi büyük yap
    const capitalizedReasonsText =
      reasonsText.charAt(0).toUpperCase() + reasonsText.slice(1);

    // Açıklama varsa ekle
    if (description) {
      return `\nAçıklama:${description}\nServis Nedeni: ${capitalizedReasonsText}`;
    }

    return `Servis Nedeni: ${capitalizedReasonsText}`;
  };

  // İşlemi onayla ve uygula
  const confirmAndProcess = async () => {
    // İşlem için verileri hazırla
    const operationData = {
      device: selectedDevice,
      connect: connectDuringOperation,
      operation: operation,
      description: getCombinedDescription(), // Birleştirilmiş açıklama
    };

    // Firma seçildiyse ekle
    if (operation === "Firma" && selectedFirm) {
      operationData.firm = selectedFirm;
      operationData.isActive = effectiveIsActiveOnTransfer;
    }

    // Servis işleminde aktiflik tercihi
    if (operation === "Servis") {
      operationData.isActive = effectiveIsActiveOnTransfer;
    }

    // Plasiyer seçildiyse ekle
    if (operation === "Plasiyer" && selectedPlaymaker) {
      operationData.playmaker = selectedPlaymaker;
    }

    // Durumu işlem başlıyor olarak ayarla ve operasyonu başlat
    setIsProcessing(true);
    onConfirm(operationData);

    // Not: İşlemin tamamlanması artık parent component'ten gelecek
    // olan props değişiklikleri ile takip edilecek
  };

  // Onay butonunun durumunu kontrol et - Sadece Servis için neden/açıklama zorunlu
  const canConfirm = () => {
    // Cihaz seçimi her durumda zorunlu
    if (!selectedDevice) return false;

    // Firma işlemi için firma seçimi zorunlu
    if (operation === "Firma" && !selectedFirm) return false;

    // Plasiyer işlemi için plasiyer seçimi zorunlu
    if (operation === "Plasiyer" && !selectedPlaymaker) return false;

    // Servis işlemi için özel kontrol - en az servis nedeni veya açıklama zorunlu
    if (operation === "Servis") {
      if (selectedReasons.length === 0 && !description) return false;
      return true;
    }

    // Diğer işlemler için açıklama zorunlu DEĞİL
    return true;
  };

  // Panel genişliğini belirleme - Servis işlemi için de geniş panel
  const getPanelWidth = () => {
    return "w-full md:w-1/2 lg:w-1/3 xl:w-1/3"; // Dar panel
  };

  // İşlem başlığını al
  const getOperationTitle = () => {
    switch (operation) {
      case "Aktiflik":
        return deviceActivityToggleLabel(selectedDevice?.isActive);
      case "Firma":
        return "Firmaya Ver";
      case "Servis":
        return "Servise Al";
      case "Plasiyer":
        return "Plasiyere Ver";
      case "Depo":
        return "Depoya Gönder";
      case "ManuelKota":
        return "Manuel Kota Yükleme";
      default:
        return "Cihaz İşlemi";
    }
  };

  // İşlem açıklamasını al
  const getOperationDescription = () => {
    const deviceName =
      selectedDevice?.deviceInfo?.deviceName ||
      selectedDevice?.deviceName ||
      "Seçili cihaz";

    switch (operation) {
      case "Aktiflik":
        return selectedDevice?.isActive
          ? `"${deviceName}" cihazı kota almaya kapatılacaktır.`
          : `"${deviceName}" cihazı kota almaya açılacaktır.`;
      case "Firma":
        return `"${deviceName}" cihazı "${selectedFirm?.firmName}" firmasına verilecektir.`;
      case "Servis":
        return `"${deviceName}" cihazı Servis birimine gönderilecektir.`;
      case "Plasiyer":
        return `"${deviceName}" cihazı "${selectedPlaymaker?.name} ${selectedPlaymaker?.lastName}" isimli plasiyere verilecektir.`;
      case "Depo":
        return `"${deviceName}" cihazı depoya gönderilecektir.`;
      case "ManuelKota":
        return `"${deviceName}" cihazına manuel olarak kota yüklenecektir.`;
      default:
        return "Bu işlemi gerçekleştirmek istediğinize emin misiniz?";
    }
  };

  // İşlem simgesini al
  const getOperationIcon = () => {
    switch (operation) {
      case "Aktiflik":
        return (
          <FaPowerOff className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />
        );
      case "Firma":
        return (
          <TbTruckDelivery className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />
        );
      case "Plasiyer":
        return (
          <FaPeopleCarry className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />
        );
      case "Depo":
        return (
          <FaWarehouse className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />
        );
      case "Servis":
        return <FaTools className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />;
      case "ManuelKota":
        return (
          <RiScales2Fill className="w-5 h-5 md:w-12 md:h-12 text-indigo-400" />
        );
      default:
        return (
          <FaExclamationTriangle className="w-5 h-5 md:w-12 md:h-12 text-amber-400" />
        );
    }
  };

  // Başarılı durumu için detaylı mesaj oluşturan fonksiyon
  const getSuccessMessage = () => {
    const deviceName =
      selectedDevice?.deviceInfo?.deviceName || selectedDevice?.deviceName;
    const serialNo = selectedDevice?.serialNo;

    let message = `"${deviceName} (${serialNo})" cihazı için `;

    switch (operation) {
      case "Aktiflik":
        message += selectedDevice?.isActive
          ? "kota almaya kapatma işlemi"
          : "kota almaya açma işlemi";
        break;
      case "Firma":
        message += `"${selectedFirm?.firmName}" firmasına verme işlemi`;
        break;
      case "Servis":
        message += "servise alma işlemi";
        if (selectedReasons.length > 0) {
          message += `\nServis Nedenleri:\n• ${selectedReasons.join("\n• ")}`;
        }
        break;
      case "Plasiyer":
        message += `"${selectedPlaymaker?.name} ${selectedPlaymaker?.lastName}" isimli plasiyere verme işlemi`;
        break;
      case "Depo":
        message += "depoya gönderme işlemi";
        break;
      default:
        message += "işlemi";
    }

    message += " başarıyla tamamlandı.";

    if (description) {
      message += `\n\nAçıklama: ${description}`;
    }

    return message;
  };
  // Adım 1: Veri Seçimi
  const renderStep1 = () => (
    <>
      {operation === "Servis" ? (
        // Servis işlemi için mevcut yan yana yerleşim
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          {/* Sağ Kolon - Servis Nedenleri */}
          <div className="w-full">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 border-b border-gray-200">
                <span className="text-md font-semibold text-gray-800 flex items-center justify-between">
                  Servis Nedenleri
                  {selectedDevice && (
                    <span className="text-md text-gray-600 font-normal">
                      {selectedDevice.deviceInfo?.deviceType ||
                        selectedDevice.deviceType}
                    </span>
                  )}
                </span>
                <div className="mt-2">
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    placeholder="Servis nedeni ara..."
                    value={serviceReasonSearchTerm}
                    onChange={(e) => setServiceReasonSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="divide-y divide-gray-200 min-h-[15vh] max-h-[18vh] overflow-y-auto">
                {filteredServiceReasons.length > 0 ? (
                  filteredServiceReasons.map((reason) => (
                    <div
                      key={reason}
                      onClick={() => toggleServiceReason(reason)}
                      className="cursor-pointer"
                    >
                      <ServiceReasonItem
                        reason={reason}
                        isSelected={selectedReasons.includes(reason)}
                      />
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-base">
                    Arama kriterlerine uygun servis nedeni bulunamadı
                  </div>
                )}
              </div>

              {selectedReasons.length > 0 && (
                <div className="p-2 bg-gray-50 border-t border-gray-200">
                  <span className="text-base text-gray-600">
                    {selectedReasons.length} neden seçildi
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : operation === "Firma" ? (
        // Firma işlemi için yan yana yerleşim
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          {/* Sağ Kolon - Firma Seçimi */}
          <div className="w-full">
            <SearchableList
              title="Firma Seçimi"
              items={filteredFirms}
              searchTerm={firmSearchTerm}
              selectedItem={selectedFirm}
              setSearchTerm={setFirmSearchTerm}
              onSelectItem={setSelectedFirm}
              renderItem={(firm) => <FirmItem firm={firm} />}
              placeholder="Firma adı veya bayser no ile ara..."
              emptyMessage="Arama kriterlerine uygun firma bulunamadı"
              className="min-h-[15vh] max-h-[18vh] overflow-y-auto"
            />
          </div>
        </div>
      ) : operation === "Plasiyer" ? (
        // Plasiyer işlemi için yan yana yerleşim
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          {/* Sağ Kolon - Plasiyer Seçimi */}
          <div className="w-full">
            <SearchableList
              title="Plasiyer Seçimi"
              items={filteredPlaymakers}
              searchTerm={playmakerSearchTerm}
              setSearchTerm={setPlaymakerSearchTerm}
              selectedItem={selectedPlaymaker}
              onSelectItem={setSelectedPlaymaker}
              renderItem={(playmaker) => (
                <PlaymakerItem playmaker={playmaker} />
              )}
              placeholder="Plasiyer adı ile ara..."
              emptyMessage="Arama kriterlerine uygun plasiyer bulunamadı"
              className="min-h-[15vh] max-h-[18vh] overflow-y-auto"
            />
          </div>
        </div>
      ) : operation === "ManuelKota" ? (
        <div className="space-y-6">
          {/* QR Input */}
          <div className="space-y-4">
            <input
              type="text"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Kod"
              className="w-full px-4 py-3 rounded-lg text-base border-2 border-gray-300 focus:border-indigo-500 outline-none"
            />

            <input
              type="text"
              value={productCounter}
              onChange={(e) => setProductCounter(e.target.value)}
              placeholder="Sayaç"
              className="w-full px-4 py-3 rounded-lg text-base border-2 border-gray-300 focus:border-indigo-500 outline-none"
            />

            <input
              type="text"
              value={productQuota}
              onChange={(e) => setProductQuota(e.target.value)}
              placeholder="Kota"
              className="w-full px-4 py-3 rounded-lg text-base border-2 border-gray-300 focus:border-indigo-500 outline-none"
            />

            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Ürün İsmi"
              className="w-full px-4 py-3 rounded-lg text-base border-2 border-gray-300 focus:border-indigo-500 outline-none"
            />

            <button
              onClick={() =>
                uploadManualQR({
                  token,
                  code,
                  serialNo: selectedDevice?.serialNo,
                  productName,
                  productQuota: Number(productQuota),
                  productCounter: Number(productCounter),
                })
              }
              disabled={
                !code ||
                !selectedDevice?.serialNo ||
                !productName ||
                !productQuota ||
                !productCounter
              }
              className={`
                w-full px-4 py-3 
                rounded-lg text-base font-medium
                ${
                  !code ||
                  !selectedDevice?.serialNo ||
                  !productName ||
                  !productQuota ||
                  !productCounter
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }
              `}
            >
              Kodu Yükle
            </button>
          </div>

          {/* Device Info */}
          {deviceInfo && (
            <div className="rounded-lg p-4 bg-gray-50">
              <div className="space-y-3">
                {[
                  { label: "Ürün", value: deviceInfo.device.type },
                  { label: "Seri No", value: deviceInfo.device.serialNo },
                  {
                    label: "Bağlanmayan Makine Kota",
                    value: deviceInfo.unConnectedDevice.quota,
                  },
                  {
                    label: "Bağlanmayan Makine Sayaç",
                    value: deviceInfo.unConnectedDevice.totalCounter,
                  },
                  {
                    label: "Sistemdeki Kota",
                    value: deviceInfo.targetProductInfo.quota,
                  },
                  {
                    label: "Sistemdeki Sayaç",
                    value: deviceInfo.targetProductInfo.totalCounter,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className="text-base font-medium text-gray-700">
                      {item.label}:
                    </span>
                    <span className="text-base text-gray-900">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Üretilen Kod Gösterimi */}
              {deviceInfo.encryptedKey && (
                <div className="mt-4">
                  <span className="text-base font-medium mb-2 text-gray-700">
                    Makine Yükleme Kodu:
                  </span>
                  <div className="relative p-4 rounded-lg bg-indigo-600 group">
                    <span className="text-white text-base break-all font-mono">
                      {deviceInfo.encryptedKey}
                    </span>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(deviceInfo.encryptedKey);
                      }}
                      className="absolute top-2 right-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    >
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                        />
                      </svg>
                    </button>

                    <div className="mt-2 pt-2 border-t border-white/20 flex justify-between items-center">
                      <span className="text-white/70 text-base">
                        Kod Uzunluğu: {deviceInfo.encryptedKey.length} karakter
                      </span>
                      <span className="text-white/70 text-base">
                        Kopyalamak için üzerine gelin
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Quota Input */}
              <div className="space-y-4 mt-4">
                <input
                  type="number"
                  value={quota}
                  onChange={(e) => setQuota(e.target.value)}
                  placeholder="Makinenin Olması Gereken Kota"
                  className="w-full px-4 py-3 rounded-lg text-base border-2 border-gray-300 focus:border-indigo-500 outline-none"
                />
                <button
                  onClick={() =>
                    quota &&
                    uploadManualQuota({
                      token,
                      code,
                      serialNo: selectedDevice?.serialNo,
                      productName,
                      productQuota: Number(productQuota),
                      productCounter: Number(productCounter),
                      newQuota: Number(quota),
                    })
                  }
                  disabled={!quota}
                  className={`
                    w-full px-4 py-3 
                    rounded-lg text-base font-medium
                    ${
                      !quota
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }
                  `}
                >
                  Kod Üret
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}

      {/* Açıklama alanı */}
      {operation !== "ManuelKota" && (
        <div className={` mb-4`}>
          <span className="block text-base font-medium text-gray-700 mb-1">
            Açıklama
          </span>
          <textarea
            className={`w-full p-2 border ${
              !description &&
              operation === "Servis" &&
              selectedReasons.length === 0
                ? "border-red-300"
                : "border-gray-300"
            } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-md`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Operasyon açıklaması giriniz (isteğe bağlı)..."
            rows={3}
          />
          {!description &&
            operation === "Servis" &&
            selectedReasons.length === 0 && (
              <p className="mt-1 text-base text-red-500">
                Servis nedeni seçiniz veya açıklama giriniz.
              </p>
            )}
        </div>
      )}

      {/* Bağlantı tercihi */}
      {operation !== "ManuelKota" && !isDealer && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <FormControlLabel
            control={
              <Checkbox
                checked={connectDuringOperation}
                onChange={(e) => setConnectDuringOperation(e.target.checked)}
                color="primary"
                size="medium"
              />
            }
            label={
              <span className="text-base text-gray-700">
                Makine bağlantısı kurulsun mu?
                <div className="text-base text-gray-500  mt-1">
                  Bu seçenek, cihaz işlemi sırasında otomatik olarak bağlantı
                  kurulmasını sağlar.
                </div>
              </span>
            }
          />
        </div>
      )}

      {/* Firmaya/Servise alırken kota alma (isActive) tercihi — cihaz bayinin eline
          geçiyorsa sorulmaz, her zaman kapalı alınır. */}
      {(operation === "Firma" || operation === "Servis") && !transferGoesToDealer && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <FormControlLabel
            control={
              <Checkbox
                checked={isActiveOnTransfer}
                onChange={(e) => setIsActiveOnTransfer(e.target.checked)}
                color="primary"
                size="medium"
              />
            }
            label={
              <span className="text-base text-gray-700">
                {operation === "Firma"
                  ? "Cihaz kota almaya açık olarak firmaya alınsın mı?"
                  : "Cihaz kota almaya açık olarak servise alınsın mı?"}
                <div className="text-base text-gray-500 mt-1">
                  {operation === "Firma"
                    ? "Varsayılan olarak cihaz kota almaya kapalı olarak firmaya alınır. Bu seçeneği işaretlerseniz cihaz kota almaya açık olarak alınır."
                    : "Varsayılan olarak cihaz kota almaya kapalı olarak servise alınır. Bu seçeneği işaretlerseniz cihaz kota almaya açık olarak alınır."}
                </div>
              </span>
            }
          />
        </div>
      )}

      {/* Seçim Özeti */}
      {operation !== "ManuelKota" && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <span className="font-medium text-base text-blue-700 mb-2">
            Seçim Özeti
          </span>
          <ul className="text-base text-blue-600 space-y-1">
            {selectedDevice && (
              <li>
                <span className="font-medium">Cihaz:</span>{" "}
                {selectedDevice.deviceInfo?.deviceName ||
                  selectedDevice.deviceName}
                <span className="ml-1 text-base text-blue-500">
                  ({selectedDevice.serialNo})
                </span>
              </li>
            )}
            {operation === "Firma" && selectedFirm && (
              <li>
                <span className="text-base font-medium">Firma:</span>{" "}
                <span className="text-base text-blue-500">
                  {selectedFirm.firmName}
                </span>
                {selectedFirm.bayserNo && (
                  <span className="ml-1 text-base text-blue-500">
                    ({selectedFirm.bayserNo})
                  </span>
                )}
              </li>
            )}
            {operation === "Plasiyer" && selectedPlaymaker && (
              <li>
                <span className="text-base font-medium">Plasiyer:</span>{" "}
                <span className="text-base text-blue-500">
                  {selectedPlaymaker.name} {selectedPlaymaker.lastName}
                </span>
              </li>
            )}
            <li>
              <span className="text-base font-medium">Bağlantı:</span>{" "}
              <span className="text-base text-blue-500">
                {connectDuringOperation ? "Evet" : "Hayır"}
              </span>
            </li>
            {(operation === "Firma" || operation === "Servis") && (
              <li>
                <span className="text-base font-medium">{DEVICE_ACTIVITY_FIELD_LABEL}:</span>{" "}
                <span className={`text-base ${effectiveIsActiveOnTransfer ? "text-green-600 font-semibold" : "text-blue-500"}`}>
                  {deviceActivityStateLabel(effectiveIsActiveOnTransfer)}
                </span>
              </li>
            )}
            {/* Servis Nedenleri - operation Servis ise ve servis nedenleri varsa */}
            {operation === "Servis" && selectedReasons.length > 0 && (
              <li className="pt-2">
                <span className="text-base font-medium">Servis Nedenleri:</span>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  {selectedReasons.map((reason) => (
                    <li key={reason} className="text-base text-blue-600">
                      {reason}
                    </li>
                  ))}
                </ul>
              </li>
            )}
            {description && (
              <li
                className={
                  operation === "Servis" && selectedReasons.length > 0
                    ? "pt-2"
                    : ""
                }
              >
                <span className="text-base font-medium">Açıklama:</span>{" "}
                <span className="text-base text-blue-500">{description}</span>
              </li>
            )}
          </ul>
        </div>
      )}
    </>
  );

  // Adım 2: Operasyon Onayı
  const renderStep2 = () => (
    <div className="flex flex-col justify-center items-center py-6">
      <div className="text-center flex flex-col items-center mb-8">
        {getOperationIcon()}
        <span className="text-xl font-bold text-gray-800 mt-4">
          {getOperationTitle()}
        </span>
        <span className="text-gray-600 mt-2 max-w-lg">
          {getOperationDescription()}
        </span>
      </div>

      <div className="bg-white p-5 rounded-lg shadow-md w-full max-w-lg">
        <span className="font-medium text-base text-gray-700 border-b pb-2">
          İşlem Detayları
        </span>

        <div className="space-y-3 mt-4">
          <div className="flex">
            <span className="font-medium text-base text-gray-600 w-1/3">
              Cihaz:
            </span>
            <span className="text-base text-gray-800">
              {selectedDevice?.deviceInfo?.deviceName ||
                selectedDevice?.deviceName}
              <span className="text-base text-gray-500 ml-1">
                ({selectedDevice?.serialNo})
              </span>
            </span>
          </div>

          {operation === "Firma" && selectedFirm && (
            <div className="flex">
              <span className="font-medium text-base text-gray-600 w-1/3">
                Firma:
              </span>
              <span className="text-base text-gray-800">
                {selectedFirm.firmName}
                {selectedFirm.bayserNo && (
                  <span className="text-base text-gray-500 ml-1">
                    ({selectedFirm.bayserNo})
                  </span>
                )}
              </span>
            </div>
          )}

          {operation === "Plasiyer" && selectedPlaymaker && (
            <div className="flex">
              <span className="font-medium text-base text-gray-600 w-1/3">
                Plasiyer:
              </span>
              <span className="text-base text-gray-800">
                {selectedPlaymaker.name} {selectedPlaymaker.lastName}
              </span>
            </div>
          )}

          {/* Servis Nedenleri - Madde madde gösterim */}
          {operation === "Servis" && selectedReasons.length > 0 && (
            <div className="flex">
              <span className="font-medium text-base text-gray-600 w-1/3">
                Servis Nedenleri:
              </span>
              <div className="flex-1">
                <ul className="list-disc pl-4 space-y-1">
                  {selectedReasons.map((reason) => (
                    <li key={reason} className="text-base text-gray-800">
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {description && (
            <div className="flex">
              <span className="font-medium text-base text-gray-600 w-1/3">
                Açıklama:
              </span>
              <span className="text-base text-gray-800 flex-1">
                {description}
              </span>
            </div>
          )}

          <div className="flex">
            <span className="font-medium text-base text-gray-600 w-1/3">
              Bağlantı Durumu:
            </span>
            <span className="text-base text-gray-800">
              {connectDuringOperation
                ? "İşlem sırasında bağlantı kurulacak"
                : "İşlem sırasında bağlantı kurulmayacak"}
            </span>
          </div>

          {(operation === "Firma" || operation === "Servis") && (
            <div className="flex">
              <span className="font-medium text-base text-gray-600 w-1/3">
                Kota Alma Durumu:
              </span>
              <span className={`text-base ${effectiveIsActiveOnTransfer ? "text-green-600 font-semibold" : "text-gray-800"}`}>
                {effectiveIsActiveOnTransfer
                  ? `Cihaz kota almaya açık olarak ${operation === "Firma" ? "firmaya" : "servise"} alınacak`
                  : `Cihaz kota almaya kapalı olarak ${operation === "Firma" ? "firmaya" : "servise"} alınacak`}
              </span>
            </div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t">
          <div className="text-base text-amber-600 flex items-center">
            <FaExclamationTriangle className="mr-2 w-5 h-5" />
            Bu işlemi onayladığınızda, yukarıdaki değişiklikler uygulanacaktır.
          </div>
        </div>
      </div>
    </div>
  );

  // Adım 3: İşlem Sonucu - İkonlar w-5 h-5 olarak güncellendi
  const renderStep3 = () => (
    <div className="flex flex-col items-center justify-center py-12">
      {isProcessing || props.deviceOperationIsLoading ? (
        <div className="text-center flex flex-col items-center">
          <div className="inline-block animate-spin text-indigo-600 mb-4">
            <FaSpinner className="w-5 h-5 md:w-10 md:h-10" />
          </div>
          <span className="text-md font-bold text-gray-800">
            İşlem Yapılıyor
          </span>
          <span className="text-base text-gray-600 mt-2">
            Lütfen bekleyin, işleminiz gerçekleştiriliyor...
          </span>
        </div>
      ) : processResult === "success" || props.deviceOperationIsSuccess ? (
        <div className="text-center flex flex-col items-center">
          <div className="text-green-500 mb-4">
            <FaCheckCircle className="w-12 h-12 inline-block" />
          </div>
          <span className="text-md font-bold text-gray-800">
            İşlem Başarılı
          </span>
          <div className="mt-4 p-4 bg-green-50 rounded-lg max-w-lg mx-auto">
            <span className="text-base text-gray-700 whitespace-pre-line">
              {props.deviceOperationSuccessMessage || getSuccessMessage()}
            </span>
          </div>
          <button
            className="mt-6 px-4 py-2 bg-indigo-600 text-white text-base rounded-md hover:bg-indigo-700 transition-colors"
            onClick={onClose}
          >
            Kapat
          </button>
        </div>
      ) : (
        <div className="text-center flex flex-col items-center">
          <div className="text-red-500 mb-4">
            <FaExclamationTriangle className="w-12 h-12 inline-block" />
          </div>
          <span className="text-md font-bold text-gray-800">
            İşlem Başarısız
          </span>
          <span className="text-base text-gray-600 mt-2 max-w-md">
            {errorMessage ||
              props.deviceOperationErrorMessage ||
              "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin."}
          </span>

          {/* İki ayrı buton: Tekrar Dene ve Geri Dön - İkon boyutları güncellendi */}
          <div className="mt-6 flex gap-4 justify-center">
            <button
              className="px-4 py-2 bg-white border border-indigo-600 text-indigo-600 text-base rounded-md hover:bg-indigo-50 transition-colors flex items-center gap-2"
              onClick={() => setCurrentStep(1)}
            >
              <FaArrowLeft className="w-5 h-5" />
              Geri Dön
            </button>

            <button
              className="px-4 py-2 bg-indigo-600 text-white text-base rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-2"
              onClick={() => {
                // Aynı verilerle işlemi tekrar başlat
                setErrorMessage("");
                setProcessResult(null);
                setIsProcessing(true);

                // İşlem verilerini hazırla
                const operationData = {
                  device: selectedDevice,
                  connect: connectDuringOperation,
                  operation: operation,
                  description: description,
                };

                // Firma seçildiyse ekle
                if (operation === "Firma" && selectedFirm) {
                  operationData.firm = selectedFirm;
                  operationData.isActive = effectiveIsActiveOnTransfer;
                }

                // Servis işleminde aktiflik tercihi
                if (operation === "Servis") {
                  operationData.isActive = effectiveIsActiveOnTransfer;
                }

                // Plasiyer seçildiyse ekle
                if (operation === "Plasiyer" && selectedPlaymaker) {
                  operationData.playmaker = selectedPlaymaker;
                }

                // İşlemi tekrar tetikle - ana component'teki onConfirm fonksiyonunu çağır
                onConfirm(operationData);
              }}
            >
              <FaRedo className="w-5 h-5" />
              Tekrar Dene
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Sağdan açılan panel - dinamik genişlik ile */}
      <div
        className={`fixed inset-y-0 right-0 ${getPanelWidth()} transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col overflow-hidden`}
      >
        {/* Panel Başlığı - Adımlara göre değişir */}
        <div className="p-4 bg-indigo-600 text-white flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {currentStep === 1 && (
              <>
                {operation === "Firma" && (
                  <TbTruckDelivery className="text-white font-SemiBold w-8 h-8" />
                )}
                {operation === "Plasiyer" && (
                  <FaPeopleCarry className="text-white font-SemiBold w-8 h-8" />
                )}
                {operation === "Depo" && (
                  <FaWarehouse className="text-white font-SemiBold w-8 h-8" />
                )}
                {operation === "Aktiflik" && (
                  <FaPowerOff className="text-white font-SemiBold w-8 h-8" />
                )}
                {operation === "Servis" && (
                  <FaTools className="text-white font-SemiBold w-8 h-8" />
                )}
                <div className="flex flex-col ">
                  <span className="text-lg tracking-widest font-SemiBold">
                    {title || "Cihaz İşlemi"}
                  </span>
                  <span className="text-md text-white tracking-widest">
                    {(selectedOperationDevice?.deviceInfo?.deviceName ||
                      selectedOperationDevice?.deviceName) +
                      " : " +
                      selectedOperationDevice?.serialNo}
                  </span>
                </div>
              </>
            )}
            {currentStep === 2 && (
              <div className="flex flex-col ">
                <h3 className="text-xl tracking-widest font-SemiBold">
                  İşlemi Onayla
                </h3>
                <h2 className="text-md text-white tracking-widest">
                  {(selectedOperationDevice?.deviceInfo?.deviceName ||
                    selectedOperationDevice?.deviceName) +
                    " : " +
                    selectedOperationDevice?.serialNo}{" "}
                </h2>
              </div>
            )}
            {currentStep === 3 && (
              <div className="flex flex-col ">
                <h3 className="text-xl tracking-widest font-SemiBold">
                  İşlem Sonucu
                </h3>
                <h2 className="text-md text-white tracking-widest">
                  {(selectedOperationDevice?.deviceInfo?.deviceName ||
                    selectedOperationDevice?.deviceName) +
                    " : " +
                    selectedOperationDevice?.serialNo}
                </h2>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing || deviceOperationIsLoading}
            className={`text-white px-1 py-2 rounded-full transition-colors ${
              isProcessing || deviceOperationIsLoading
                ? "opacity-40 cursor-not-allowed"
                : "hover:text-indigo-200 hover:bg-indigo-700"
            }`}
          >
            <FaTimes className="w-8 h-8 pl-1" />
          </button>
        </div>

        {/* Adım Göstergesi */}
        {operation !== "ManuelKota" && (
          <div className="bg-indigo-100 px-4 py-2">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              <div
                className={`flex flex-col items-center ${
                  currentStep >= 1 ? "text-indigo-600" : "text-gray-400"
                }`}
              >
                <span
                  className={`w-8 h-8 text-base  rounded-full flex items-center justify-center ${
                    currentStep >= 1
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  1
                </span>
                <span className="text-base mt-1">Seçim</span>
              </div>

              <div
                className={`flex-1 h-1 mx-2 ${
                  currentStep >= 2 ? "bg-indigo-600" : "bg-gray-200"
                }`}
              ></div>

              <div
                className={`flex flex-col items-center ${
                  currentStep >= 2 ? "text-indigo-600" : "text-gray-400"
                }`}
              >
                <span
                  className={`w-8 h-8 text-base rounded-full flex items-center justify-center ${
                    currentStep >= 2
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  2
                </span>
                <span className="text-base mt-1">Onay</span>
              </div>

              <div
                className={`flex-1 h-1 mx-2 ${
                  currentStep >= 3 ? "bg-indigo-600" : "bg-gray-200"
                }`}
              ></div>

              <div
                className={`flex flex-col items-center ${
                  currentStep >= 3 ? "text-indigo-600" : "text-gray-400"
                }`}
              >
                <div
                  className={`w-8 h-8 text-base rounded-full flex items-center justify-center ${
                    currentStep >= 3
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  3
                </div>
                <span className="text-base mt-1">Sonuç</span>
              </div>
            </div>
          </div>
        )}

        {/* Panel İçeriği - Adımlara göre değişir */}
        <div className="flex-1 overflow-y-auto p-4 bg-white">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && operation !== "ManuelKota" && renderStep2()}
          {currentStep === 3 && operation !== "ManuelKota" && renderStep3()}
        </div>

        {/* Alt Butonlar - Adımlara göre değişir */}
        {currentStep === 1 && operation !== "ManuelKota" && (
          <div className="p-4 bg-gray-50 border-t flex justify-between gap-4 sticky bottom-0 z-10">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
              onClick={onClose}
            >
              <FaTimes className="text-gray-500 w-5 h-5" />
              <span className="text-base">İptal</span>
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 ${
                canConfirm()
                  ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              } rounded-md transition-colors`}
              onClick={goToNextStep}
              disabled={!canConfirm()}
            >
              <FaAngleRight
                className={`w-5 h-5 ${
                  canConfirm() ? "text-white" : "text-gray-400"
                }`}
              />
              <span className="text-base">Devam Et</span>
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="p-4 bg-gray-50 border-t flex justify-between sticky bottom-0 z-10">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
              onClick={goToPreviousStep}
            >
              <FaArrowLeft className="text-gray-500 w-5 h-5" />
              <span className="text-base">Geri</span>
            </button>

            <button
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
              onClick={() => {
                goToNextStep();
                confirmAndProcess();
              }}
            >
              <FaCheck className="text-white w-5 h-5" />
              <span className="text-base">Onayla</span>
            </button>
          </div>
        )}

        {/* Adım 3'te alt butonlara gerek yok, içerikte var */}
      </div>

      {/* Arkaplan overlay - yüksek zIndex ile */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 md:bg-opacity-20 z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={isProcessing || deviceOperationIsLoading ? undefined : onClose}
        />
      )}
    </>
  );
}

export default DeviceOperationPanel;
